// ═══════════════════════════════════════════════════════════════
// SummarizerModelPicker — Custom combobox pour sélection modèle
// ITRIIA BENCH v0.1
// ═══════════════════════════════════════════════════════════════
//
// Remplace le <select> natif du SummarizerOverrideCard.
// Affichage enrichi avec tooltips au survol.
//
// Props:
//   - value: clé symbolique du modèle sélectionné ('' = défaut template)
//   - onChange(symbolicKey): callback au changement
//   - disabled: boolean (état sauvegarde)
//   - templateDefault: clé symbolique du modèle par défaut du template
//   - style: inline styles CSS optionnels pour le trigger
// ═══════════════════════════════════════════════════════════════

import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check } from 'lucide-react';
import { MODEL_PROFILES, PICKER_ORDER, BENCH_RUN } from './modelProfiles';
import ModelProfileTooltip from './ModelProfileTooltip';

function SummarizerModelPicker({ value, onChange, disabled, templateDefault, style }) {
    const [isOpen, setIsOpen] = useState(false);
    const [hoveredSymbolic, setHoveredSymbolic] = useState(null);
    const [tooltipPos, setTooltipPos] = useState(null);
    const triggerRef = useRef(null);
    const dropdownRef = useRef(null);
    const optionsRef = useRef({});

    // Fermer le dropdown au clic en dehors
    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (e) => {
            if (
                triggerRef.current &&
                !triggerRef.current.contains(e.target) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(e.target)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    // Calculer position tooltip au survol — viewport-aware (droite ou gauche selon l'espace)
    const handleOptionHover = (symbolic, e) => {
        if (!e.currentTarget || !triggerRef.current) return;
        const itemRect = e.currentTarget.getBoundingClientRect();
        const triggerRect = triggerRef.current.getBoundingClientRect();
        const tooltipWidth = 340; // 320px content + marge
        const spaceRight = window.innerWidth - triggerRect.right;
        const spaceLeft = triggerRect.left;

        // Flip : à gauche du trigger si pas assez de place à droite
        const left = spaceRight >= tooltipWidth
            ? triggerRect.right + 12
            : spaceLeft >= tooltipWidth
                ? triggerRect.left - tooltipWidth
                : 12; // fallback bord gauche viewport

        // Clamp vertical pour ne pas sortir du viewport
        const maxTop = window.innerHeight - 400;
        const top = Math.max(8, Math.min(itemRect.top, maxTop));

        setTooltipPos({ top, left });
        setHoveredSymbolic(symbolic);
    };

    // Sélectionner une option
    const handleSelectOption = (symbolic) => {
        onChange(symbolic);
        setIsOpen(false);
        setHoveredSymbolic(null);
    };

    // Profil actuellement sélectionné
    const selectedProfile = value ? MODEL_PROFILES[value] : null;
    const defaultProfile = templateDefault ? MODEL_PROFILES[templateDefault] : null;

    // État d'affichage du trigger
    const triggerLabel = value
        ? MODEL_PROFILES[value]?.label || value
        : `Défaut du template${templateDefault ? ` (${defaultProfile?.label})` : ''}`;

    const triggerScore = value ? MODEL_PROFILES[value]?.bench.score : defaultProfile?.bench.score;
    const triggerColor = value ? MODEL_PROFILES[value]?.tier_color : defaultProfile?.tier_color;

    const triggerStyle = {
        padding: '10px 14px',
        borderRadius: '10px',
        border: `1px solid ${disabled ? 'var(--card-border)' : 'rgba(139,92,246,0.3)'}`,
        background: 'var(--input-bg, var(--card-bg))',
        color: 'var(--text)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        fontSize: '13px',
        fontWeight: 500,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px',
        transition: 'all 0.15s ease',
        minWidth: '200px',
        ...style,
    };

    const dropdownStyle = {
        position: 'absolute',
        top: triggerRef.current ? `${triggerRef.current.offsetHeight + 6}px` : '100%',
        left: 0,
        right: 0,
        width: '100%',
        minWidth: '250px',
        maxHeight: '320px',
        overflowY: 'auto',
        zIndex: 100,
        background: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: '12px',
        boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
        animation: 'fadeIn 0.15s ease',
    };

    const optionRowStyle = (symbolic) => ({
        padding: '10px 14px',
        cursor: 'pointer',
        borderLeft: `3px solid ${value === symbolic ? '#8b5cf6' : 'transparent'}`,
        background: value === symbolic ? 'rgba(139,92,246,0.08)' : 'transparent',
        transition: 'all 0.12s ease',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px',
        ':hover': {
            background: 'rgba(139,92,246,0.08)',
        },
    });

    const optionLabelStyle = {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
    };

    const optionModelNameStyle = {
        fontSize: '13px',
        fontWeight: 500,
        color: 'var(--text)',
    };

    const optionProviderStyle = {
        fontSize: '11px',
        color: 'var(--text-muted)',
    };

    const optionChipsStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
    };

    const optionChipStyle = (color) => ({
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '10px',
        fontWeight: 600,
        backgroundColor: `${color}20`,
        border: `1px solid ${color}40`,
        color: color,
        whiteSpace: 'nowrap',
    });

    const optionScoreBadgeStyle = {
        padding: '2px 6px',
        borderRadius: '4px',
        fontSize: '11px',
        fontWeight: 600,
        background: 'var(--card-border)',
        color: 'var(--text-secondary)',
        whiteSpace: 'nowrap',
    };

    const defaultOptionStyle = {
        padding: '10px 14px',
        cursor: 'pointer',
        borderLeft: `3px solid ${value === '' ? '#8b5cf6' : 'transparent'}`,
        background: value === '' ? 'rgba(139,92,246,0.08)' : 'transparent',
        transition: 'all 0.12s ease',
        fontStyle: 'italic',
        color: 'var(--text-secondary)',
        fontSize: '13px',
        borderBottom: '1px solid var(--card-border)',
        ':hover': {
            background: 'rgba(139,92,246,0.08)',
        },
    };

    const defaultOptionHintStyle = {
        fontSize: '10px',
        color: 'var(--text-muted)',
        marginTop: '2px',
        fontStyle: 'normal',
    };

    const hoveredProfile = hoveredSymbolic ? MODEL_PROFILES[hoveredSymbolic] : null;

    return (
        <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
            {/* Trigger — affichage fermé */}
            <button
                ref={triggerRef}
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
                style={triggerStyle}
                onMouseEnter={() => setHoveredSymbolic(null)}
            >
                <div style={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
                    <div style={{ fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {triggerLabel}
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                    {triggerScore && (
                        <span style={optionScoreBadgeStyle}>
                            {triggerScore}/25
                        </span>
                    )}
                    <ChevronDown
                        size={16}
                        style={{
                            color: 'var(--text-secondary)',
                            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.15s ease',
                        }}
                    />
                </div>
            </button>

            {/* Dropdown — options */}
            {isOpen && (
                <div ref={dropdownRef} style={dropdownStyle}>
                    {/* Option "Défaut du template" */}
                    <div
                        style={defaultOptionStyle}
                        onClick={() => handleSelectOption('')}
                        onMouseEnter={(e) => {
                            if (defaultProfile) handleOptionHover('', e);
                            else setHoveredSymbolic(null);
                        }}
                        onMouseLeave={() => setHoveredSymbolic(null)}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                            <div style={{ flex: 1 }}>
                                <div>Défaut du template</div>
                                {defaultProfile && (
                                    <div style={defaultOptionHintStyle}>
                                        {defaultProfile.label}
                                    </div>
                                )}
                            </div>
                            {value === '' && <Check size={16} style={{ color: '#8b5cf6', flexShrink: 0 }} />}
                        </div>
                    </div>

                    {/* Options modèles — dans PICKER_ORDER */}
                    {PICKER_ORDER.map((symbolic) => {
                        const profile = MODEL_PROFILES[symbolic];
                        if (!profile) return null;

                        return (
                            <div
                                key={symbolic}
                                ref={(el) => { optionsRef.current[symbolic] = el; }}
                                style={{
                                    ...optionRowStyle(symbolic),
                                    background: hoveredSymbolic === symbolic ? 'rgba(139,92,246,0.08)' : (
                                        value === symbolic ? 'rgba(139,92,246,0.08)' : 'transparent'
                                    ),
                                }}
                                onClick={() => handleSelectOption(symbolic)}
                                onMouseEnter={(e) => handleOptionHover(symbolic, e)}
                                onMouseLeave={() => setHoveredSymbolic(null)}
                            >
                                <div style={optionLabelStyle}>
                                    <div style={optionModelNameStyle}>
                                        {profile.label}
                                    </div>
                                    <div style={optionProviderStyle}>
                                        {profile.provider} · <span style={{ color: 'var(--text-secondary)' }}>${profile.bench.cost_per_point}/pt</span>
                                    </div>
                                </div>
                                <div style={optionChipsStyle}>
                                    <span style={optionChipStyle(profile.tier_color)}>
                                        {profile.tier_label}
                                    </span>
                                    <span style={optionScoreBadgeStyle}>
                                        {profile.bench.score}/25
                                    </span>
                                    {value === symbolic && (
                                        <Check size={16} style={{ color: '#8b5cf6', flexShrink: 0 }} />
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Tooltip au survol — Portal pour échapper à tout overflow/stacking context parent */}
            {isOpen && hoveredProfile && tooltipPos && createPortal(
                <div style={{
                    position: 'fixed',
                    top: tooltipPos.top,
                    left: tooltipPos.left,
                    pointerEvents: 'none',
                    zIndex: 99999,
                }}>
                    <ModelProfileTooltip profile={hoveredProfile} />
                </div>,
                document.body
            )}

            {/* Animation fadeIn */}
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-4px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
}

export default SummarizerModelPicker;
